import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  VOICES,
  STT_LANGS,
  PAUSES,
  getPrefs,
  setPrefs,
  resetPrefs,
} from "../agent/copilotPrefs";
import { ttsUrl, withCredential, stopSpeaking, micProbe, openMeter } from "../agent/voice";

const PREVIEW_LINE =
  "Found about 8 thousand sales leaders at B2B SaaS companies. Want just directors and up, or one industry?";

const Choice = ({ options, value, onChange }) => (
  <div className="copilotSettings__seg">
    {options.map((o) => (
      <button
        type="button"
        key={o.id}
        className={`copilotSettings__segBtn${value === o.id ? " is-on" : ""}`}
        onClick={() => onChange(o.id)}
      >
        {o.name}
      </button>
    ))}
  </div>
);

const CopilotSettingsPopup = ({ onClose, anchorRef }) => {
  const [prefs, setLocal] = useState(getPrefs());
  const [playingId, setPlayingId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const current = VOICES.find(v => v.id === prefs.voice) || VOICES[0];
  const previewRef = useRef(null);
  const rootRef = useRef(null);
  const [pos, setPos] = useState(null);
  const meterRef = useRef(null);
  const update = (patch) => setLocal(setPrefs(patch));

  // Rendered into <body>, placed by the room actually available: above the
  // trigger, below it, or centred when neither side fits (short windows and
  // phones). Anchored inside the composer it inherited that flex row, its
  // clipping and its scroll, and fought all three.
  useLayoutEffect(() => {
    const anchor = anchorRef && anchorRef.current;
    const box = rootRef.current;
    if (!anchor || !box) return undefined;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const w = box.offsetWidth || 400;
      const h = box.offsetHeight || 340;
      const gap = 10;
      const edge = 12;
      const left = Math.max(edge, Math.min(a.right - w, window.innerWidth - w - edge));
      if (h + gap <= a.top - edge) setPos({ left, top: a.top - h - gap });
      else if (h + gap <= window.innerHeight - a.bottom - edge) setPos({ left, top: a.bottom + gap });
      else
        setPos({
          left: Math.max(edge, (window.innerWidth - w) / 2),
          top: Math.max(edge, (window.innerHeight - h) / 2),
          centred: true
        });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef, menuOpen]);

  // Click anywhere outside (the gear included, it toggles) closes it.
  useEffect(() => {
    const onDown = (e) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(e.target) &&
        !e.target.closest(".copilotPage__settingsBtn")
      )
        onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  const stopPreview = () => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
    }
    setPlayingId(null);
  };
  const preview = (voiceId) => {
    if (playingId === voiceId) {
      stopPreview();
      return;
    }
    stopPreview();
    stopSpeaking();
    setPlayingId(voiceId);
    withCredential((token) => {
      const a = new Audio(ttsUrl(PREVIEW_LINE, token, voiceId));
      a.playbackRate = prefs.speed === "fast" ? 1.12 : 1;
      previewRef.current = a;
      a.onended = () =>
        setPlayingId((current) => (current === voiceId ? null : current));
      a.onerror = a.onended;
      a.play().catch(a.onerror);
    });
  };
  useEffect(() => stopPreview, []);

  // A live meter under the sensitivity control: the bar is what the mic
  // hears, the notch is where words start being accepted. Tuning this by
  // description alone is guesswork.
  useEffect(() => {
    const close = openMeter();
    let raf = 0;
    const paint = () => {
      const el = meterRef.current;
      const probe = micProbe();
      if (el && probe) {
        el.style.setProperty("--level", probe.level.toFixed(3));
        el.style.setProperty("--threshold", probe.threshold.toFixed(3));
      }
      raf = requestAnimationFrame(paint);
    };
    paint();
    return () => {
      cancelAnimationFrame(raf);
      close();
    };
  }, []);

  return ReactDOM.createPortal(
    <div
      className="copilotSettings"
      role="dialog"
      aria-label="Voice settings"
      ref={rootRef}
      data-placement={pos && pos.centred ? "centre" : "anchored"}
      style={pos ? { left: `${pos.left}px`, top: `${pos.top}px` } : { visibility: "hidden" }}
    >
      <div className="copilotSettings__head">
        <span>Voice</span>
        <button
          type="button"
          className="copilotSettings__close"
          onClick={onClose}
          aria-label="Close"
        >
          <i className="material-icons-outlined">close</i>
        </button>
      </div>

      <div className="copilotSettings__row copilotSettings__row--voice">
        <span>Voice</span>
        <div className="copilotSettings__voicePick">
          <button
            type="button"
            className={`copilotSettings__voiceBtn${menuOpen ? " is-open" : ""}`}
            onClick={() => setMenuOpen(open => !open)}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
          >
            <span className="copilotSettings__voiceName">{current.name}</span>
            <span className="copilotSettings__voiceNote">{current.note}</span>
            <i className="material-icons-round">expand_more</i>
          </button>
          <button
            type="button"
            className="copilotSettings__play"
            title="Preview"
            onClick={() => preview(prefs.voice)}
          >
            <i className="material-icons-round">{playingId === prefs.voice ? "stop" : "play_arrow"}</i>
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="copilotSettings__menu" role="listbox">
          {["f", "m"].map(g => (
            <React.Fragment key={g}>
              <div className="copilotSettings__group">{g === "f" ? "Female" : "Male"}</div>
              {VOICES.filter(v => v.gender === g).map(v => (
                <div
                  key={v.id}
                  role="option"
                  aria-selected={prefs.voice === v.id}
                  className={`copilotSettings__option${prefs.voice === v.id ? " is-on" : ""}`}
                  onClick={() => {
                    update({ voice: v.id });
                    setMenuOpen(false);
                  }}
                >
                  <i className="material-icons-round">{prefs.voice === v.id ? "check" : ""}</i>
                  <span className="copilotSettings__voiceName">{v.name}</span>
                  <span className="copilotSettings__voiceNote">{v.note}</span>
                  <button
                    type="button"
                    className="copilotSettings__play"
                    title="Preview"
                    onClick={e => {
                      e.stopPropagation();
                      preview(v.id);
                    }}
                  >
                    <i className="material-icons-round">{playingId === v.id ? "stop" : "play_arrow"}</i>
                  </button>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="copilotSettings__grid">
      <div className="copilotSettings__row">
        <span>Speed</span>
        <Choice
          options={[
            { id: "normal", name: "Normal" },
            { id: "fast", name: "Fast" },
          ]}
          value={prefs.speed}
          onChange={(speed) => update({ speed })}
        />
      </div>
      <div className="copilotSettings__row">
        <span>Read replies aloud</span>
        <Choice
          options={[
            { id: "talk", name: "In voice chat" },
            { id: "never", name: "Never" },
          ]}
          value={prefs.readAloud}
          onChange={(readAloud) => update({ readAloud })}
        />
      </div>
      <div className="copilotSettings__row">
        <span>Interrupt by speaking</span>
        <Choice
          options={[
            { id: "on", name: "On" },
            { id: "off", name: "Off" },
          ]}
          value={prefs.bargeIn ? "on" : "off"}
          onChange={(v) => update({ bargeIn: v === "on" })}
        />
      </div>
      <div className="copilotSettings__row">
        <span>Ignore background voices</span>
        <Choice
          options={[{ id: "on", name: "On" }, { id: "off", name: "Off" }]}
          value={prefs.nearFieldOnly ? "on" : "off"}
          onChange={v => update({ nearFieldOnly: v === "on" })}
        />
      </div>
      <div className="copilotSettings__row copilotSettings__row--meter">
        <span>Mic sensitivity</span>
        <Choice
          options={[{ id: "low", name: "Low" }, { id: "normal", name: "Normal" }, { id: "high", name: "High" }]}
          value={prefs.micSensitivity}
          onChange={micSensitivity => update({ micSensitivity })}
        />
        <div className="copilotSettings__meter" ref={meterRef} aria-hidden="true">
          <span className="copilotSettings__meterFill" />
          <span className="copilotSettings__meterMark" />
        </div>
      </div>
      <div className="copilotSettings__row">
        <span>Pause before sending</span>
        <Choice options={PAUSES} value={prefs.pauseMs} onChange={pauseMs => update({ pauseMs })} />
      </div>
      <div className="copilotSettings__row">
        <span>Dictation language</span>
        <Choice
          options={STT_LANGS}
          value={prefs.sttLang}
          onChange={(sttLang) => update({ sttLang })}
        />
      </div>
      </div>
      <div className="copilotSettings__foot">
        <span>Saved on this browser.</span>
        <button
          type="button"
          className="copilotSettings__reset"
          onClick={() => setLocal(resetPrefs())}
        >
          Reset to defaults
        </button>
      </div>
    </div>,
    document.body
  );
};

export default CopilotSettingsPopup;
